import { PopoverOverlayT } from '@/components/common/propertyPopoverOverlay/PopoverOverlay.tsx'
import { useHandleProperties } from '@/components/common/propertyPopoverOverlay/useHandleProperties.tsx'
import Property from '@/components/common/propertyPopoverOverlay/Property.tsx'
import SelectFallback from '@/components/common/propertyPopoverOverlay/overlay/SelectFallback.tsx'
import { useTranslation } from 'react-i18next'
import PropertyGoBackNew from '../PropertyGoBackNew'
import { useEffect, useMemo, useState, type UIEvent } from 'react'
import { Input } from '@/components/ui/Input.tsx'
import { useOverlayStore } from '@/components/common/propertyPopoverOverlay/overlay/useOverlayStore.ts'

type PropsT = PopoverOverlayT & {
  searchLabel?: string
  handleClose: () => void
  open: boolean
}

export default function OverlaySelectOpened({
  properties,
  handleClose,
  showSearch,
  open,
  selectAll,
  ...props
}: PropsT) {
  const {
    setCurrentProperties,
    currentProperties,
    handleGoBack,
    propertiesFlat,
    selectedNestedProperties,
    handleSelectParent,
    handleValueChange,
    pureProps,
  } = useHandleProperties(
    properties,
    props.onSelectParent,
    open,
    selectAll,
    props.showAllSearchResults,
    props.debug,
  )
  const { t } = useTranslation()
  const setSwipeDownDisabled = useOverlayStore(state => state.setSwipeDownDisabled)
  const [searchValue, setSearchValue] = useState('')

  useEffect(() => {
    if (open && selectedNestedProperties.length === 0) {
      // prevent resetting when selecting nested property
      setCurrentProperties(properties)
    }
  }, [open, properties, setCurrentProperties, selectedNestedProperties.length])

  useEffect(() => {
    if (!open) {
      setSearchValue('')
      setSwipeDownDisabled(false)
    }
  }, [open, setSwipeDownDisabled])

  const displayedProperties = useMemo(() => {
    const search = searchValue.trim().toLowerCase()
    if (!search) return currentProperties

    const searchBase = props.selectedParent
      ? propertiesFlat.filter(property => property.parent === props.selectedParent)
      : propertiesFlat

    if (props.showAllSearchResults) return searchBase

    return searchBase.filter(property => {
      const searchableValue = property.searchableValue || property.label
      return searchableValue.toLowerCase().includes(search)
    })
  }, [
    currentProperties,
    propertiesFlat,
    props.selectedParent,
    props.showAllSearchResults,
    searchValue,
  ])

  function handleSearchValueChange(value: string) {
    setSearchValue(value)
    handleValueChange(value)
    props.handleSearch?.(value)
  }

  function handleScroll(event: UIEvent<HTMLDivElement>) {
    setSwipeDownDisabled(event.currentTarget.scrollTop > 5)
  }

  if (pureProps.length === 0) {
    return (
      <SelectFallback
        fallbackMessage={props.fallbackMessage ?? t(`common.noDataFound`)}
        onClick={handleClose}
      />
    )
  }

  return (
    <div className={`flex min-h-0 flex-1 flex-col pt-2`}>
      {showSearch && (
        <div className={`shrink-0 border-b px-3 pb-2`}>
          <Input
            value={searchValue}
            // The prop was already arriving here and being thrown away, so the
            // phone showed a generic "Szukaj..." while the desktop popover showed
            // whatever the caller asked for — the same control saying two things.
            placeholder={props.searchLabel ?? `${t(`common.search`)}...`}
            className={`h-12 text-base`}
            onChange={event => handleSearchValueChange(event.target.value)}
          />
        </div>
      )}
      <div
        tabIndex={0}
        onScroll={handleScroll}
        className={`min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-1`}
      >
        {displayedProperties.length < 1 ? (
          <SelectFallback
            fallbackMessage={props.fallbackMessage ?? t(`common.noDataFound`)}
            className={`px-2 pb-1 pt-2`}
          />
        ) : (
          displayedProperties.map((property, index) => {
            return (
              <Property
                key={`${property.id || property.value}_${index}`}
                setCurrentProperties={setCurrentProperties}
                property={property}
                handleOpenChange={handleClose}
                parent={`overlay`}
                handleSelectParent={handleSelectParent}
                renderAsButton
                {...props}
              />
            )
          })
        )}
        {selectedNestedProperties.length > 0 && (
          <PropertyGoBackNew handleGoBack={handleGoBack} parent={`overlay`} />
        )}
      </div>
    </div>
  )
}
